import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { chatApi, profileApi } from "../lib/api";
import { playPing, playLoudChime } from "../lib/sound";
import { useAuth } from "./AuthContext";
import { useIsMobile } from "../components/mobile/DesktopOnlyScreen";

const ChatContext = createContext(null);

export const useChat = () => useContext(ChatContext);

const UNREAD_POLL_MS = 4000;
const HEARTBEAT_MS = 30000;

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const authed = user && user !== false && user.role !== "super_admin";

  const [unread, setUnread] = useState({ total: 0, mentions: 0, byConversation: {} });
  const prevTotal = useRef(0);
  const muteNotifyUntil = useRef(0); // suppress notification right after we send/read
  const notifyReady = useRef(false);

  const suppressNextNotify = useCallback(() => {
    muteNotifyUntil.current = Date.now() + 1500;
    prevTotal.current = 0; // re-baseline so a drop doesn't get treated oddly
  }, []);

  const refreshUnread = useCallback(async () => {
    try {
      const u = await chatApi.unread();
      setUnread(u);
      return u;
    } catch {
      return null;
    }
  }, []);

  // Ask for browser notification permission once (after auth).
  useEffect(() => {
    if (!authed) return;
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    notifyReady.current = true;
  }, [authed]);

  // Poll unread + fire toast / desktop notification on increase.
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    let timer;
    const tick = async () => {
      const u = await refreshUnread();
      if (!cancelled && u) {
        const grew = u.total > prevTotal.current && prevTotal.current !== 0;
        const onMessages = window.location.pathname.startsWith("/messages");
        if (grew && Date.now() > muteNotifyUntil.current && !onMessages) {
          const body = u.mentions > 0 ? "You were mentioned in a message" : "You have new messages";
          if (isMobile) {
            // Mobile gets its own larger, top-anchored, longer-lived banner
            // (MobileNewMessageBanner) with a louder, more prominent chime —
            // desktop's toast position/sound/duration are left untouched.
            playLoudChime();
            window.dispatchEvent(new CustomEvent("chat:new-message-banner", { detail: { title: "New message", body } }));
          } else {
            playPing();
            toast.message("New message", {
              description: body,
              action: { label: "Open", onClick: () => navigate("/messages") },
            });
          }
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
              const n = new Notification("ServiceGuru Pro", { body, icon: "/brand-logo.png" });
              n.onclick = () => {
                window.focus();
                navigate("/messages");
                n.close();
              };
            } catch {
              /* notifications unsupported */
            }
          }
        }
        prevTotal.current = u.total;
      }
      if (!cancelled) timer = setTimeout(tick, UNREAD_POLL_MS);
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [authed, refreshUnread, navigate, isMobile]);

  // Presence heartbeat while the app is open.
  useEffect(() => {
    if (!authed) return;
    profileApi.heartbeat().catch(() => {});
    const id = setInterval(() => profileApi.heartbeat().catch(() => {}), HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [authed]);

  // ---- Real-time WebSocket ----
  // Connects to /api/chat/ws (cookie-auth) and re-broadcasts every server
  // event as a `chat-event` window CustomEvent so every mounted chat view
  // (desktop Messages, MobileMessages, thread panels) can react instantly
  // without polling. Auto-reconnects with exponential backoff.
  useEffect(() => {
    if (!authed) return;
    let ws;
    let closed = false;
    let backoff = 500;
    const connect = () => {
      if (closed) return;
      try {
        const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const wsUrl = base.replace(/^http/, "ws") + "/api/chat/ws";
        ws = new WebSocket(wsUrl);
        ws.onopen = () => { backoff = 500; };
        ws.onmessage = (e) => {
          let data;
          try { data = JSON.parse(e.data); } catch { return; }
          if (!data || !data.type) return;
          // Emit locally — chat pages listen and merge into their state.
          window.dispatchEvent(new CustomEvent("chat-event", { detail: data }));
          // Keep unread badge in sync on any incoming message.
          if (data.type === "chat_message" || data.type === "chat_message_updated" || data.type === "chat_message_deleted") {
            refreshUnread();
          }
        };
        ws.onclose = () => {
          if (closed) return;
          const wait = Math.min(backoff, 15000);
          setTimeout(connect, wait);
          backoff = Math.min(backoff * 2, 15000);
        };
        ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
      } catch {
        // fallback: just retry later
        setTimeout(connect, 2000);
      }
    };
    connect();
    return () => {
      closed = true;
      try { ws && ws.close(); } catch { /* noop */ }
    };
  }, [authed, refreshUnread]);

  return (
    <ChatContext.Provider value={{ unread, refreshUnread, suppressNextNotify }}>
      {children}
    </ChatContext.Provider>
  );
}
